import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig';
import DirectChat from '../components/DirectChat';
import { clearPendingNotificationRedirect } from '../utils/authVerification';

function waitForAuthReady(auth) {
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

export default function DirectChatRoute() {
  const { chatId } = useParams();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const normalizeNavigateState = (state) => {
    if (!state || typeof state !== 'object') {
      return null;
    }

    if ('nativeEvent' in state || 'target' in state || 'currentTarget' in state) {
      return null;
    }

    return state;
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const auth = getAuth();
        const authUser = await waitForAuthReady(auth);

        if (cancelled) {
          return;
        }

        if (!authUser) {
          clearPendingNotificationRedirect();
          navigate('/messages', { replace: true });
          return;
        }

        const cached = localStorage.getItem('currentUserProfile');
        let cachedProfile = null;
        if (cached) {
          try {
            cachedProfile = JSON.parse(cached);
          } catch (error) {
            console.warn('Failed to parse cached currentUserProfile:', error);
          }
        }

        const meUid = authUser.uid;
        const routeUser = cachedProfile?.uid === meUid
          ? { ...cachedProfile, uid: meUid }
          : { uid: meUid };

        setCurrentUser(routeUser);

        const convoRef = doc(db, 'conversations', chatId);
        const convoSnap = await getDoc(convoRef);

        if (cancelled) {
          return;
        }

        if (!convoSnap.exists()) {
          clearPendingNotificationRedirect();
          navigate('/messages', { replace: true });
          return;
        }

        const convo = convoSnap.data();
        if (convo.deleting === true) {
          clearPendingNotificationRedirect();
          const deletedByOther = convo.deleteRequestedBy && convo.deleteRequestedBy !== meUid;
          navigate('/messages', {
            replace: true,
            state: deletedByOther ? { convoDeleted: true } : null,
          });
          return;
        }

        if (convo.isGroup) {
          // This route is for DMs only
          clearPendingNotificationRedirect();
          setNotFound(true);
          return;
        }

        // 3) Determine "me" and "other" from members (no string parsing)
        const members = Array.isArray(convo.members) ? convo.members : [];
        if (members.length < 1) {
          clearPendingNotificationRedirect();
          setNotFound(true);
          return;
        }

        if (!meUid || !members.includes(meUid)) {
          clearPendingNotificationRedirect();
          navigate('/messages', { replace: true });
          return;
        }

        let otherUid = null;
        if (meUid) {
          otherUid = members.find((u) => u !== meUid) || members[0];
        } else {
          // if we don't know me, just pick the first member; DirectChat still works for read
          otherUid = members[0];
        }

        if (!otherUid) {
          clearPendingNotificationRedirect();
          setNotFound(true);
          return;
        }

        // 4) Prefer embedded profile from conversation; fall back to users doc
        const embedded = convo.memberProfiles?.[otherUid];
        const needsHydration =
          !embedded ||
          !embedded.avatarUrl ||
          !embedded.rank;

        if (needsHydration) {
          try {
            const otherSnap = await getDoc(doc(db, 'users', otherUid));
            if (cancelled) {
              return;
            }

            if (otherSnap.exists()) {
              setOtherUser({ id: otherUid, ...embedded, ...otherSnap.data() });
            } else if (embedded) {
              setOtherUser({ id: otherUid, ...embedded });
            } else {
              setOtherUser({ id: otherUid });
            }
          } catch (error) {
            if (error?.code !== 'permission-denied') {
              throw error;
            }

            if (!cancelled) {
              setOtherUser(embedded ? { id: otherUid, ...embedded } : { id: otherUid });
            }
          }
        } else {
          setOtherUser({ id: otherUid, ...embedded });
        }
        clearPendingNotificationRedirect();
      } catch (e) {
        console.error('Failed to load direct chat:', e);
        clearPendingNotificationRedirect();
        navigate('/messages', { replace: true });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [chatId, navigate]);

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (notFound) {
    return (
      <div style={{ padding: 16 }}>
        <p>Conversation not found.</p>
        <button onClick={() => navigate('/messages')}>Back to Messages</button>
      </div>
    );
  }
  if (!currentUser || !otherUser) {
    return (
      <div style={{ padding: 16 }}>
        <p>Couldn’t load participants.</p>
        <button onClick={() => navigate('/messages')}>Back to Messages</button>
      </div>
    );
  }

  return (
    <DirectChat
      chatId={chatId}
      currentUser={currentUser}
      otherUser={otherUser}
      onBack={(state = null) => navigate('/messages', {
        replace: true,
        state: normalizeNavigateState(state),
      })}
    />
  );
}
